// Global Instructions Rule Applied!
//
// HTTP layer for user rights in Configuration (docs/USER_RIGHTS_DESIGN.md): the
// roles, and one capability's per-record rules. First consumer: the Pipelines
// panel's "Who can move" column (capability pipeline_stage.move, one rule per
// stage). Then Configuration -> Users & Roles (section 7): the roles and their
// members, the people who may sign in, and the rights matrix. Talks to the Python backend only. Every response is Zod-validated; a
// response that fails validation is an error, never coerced.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl, type RouteDefinition } from '../../../core/utils/endpoints.ts'
import { readBackendErrorMessage, readJsonBody } from '../../../core/utils/utilities.ts'
import {
  RoleModelSchema,
  CapabilityOverridesModelSchema,
  CapabilityOverrideStateSchema,
  UserRightsRoleSchema,
  UserRightsUserSchema,
  UserRightsUserOptionSchema,
  UserRightsCapabilitySchema,
  RoleSavedSchema,
  RoleTrashedSchema,
  RoleMemberStateSchema,
  CapabilityDefaultStateSchema,
  type RoleModel,
  type CapabilityOverridesModel,
  type CapabilityOverrideState,
  type UserRightsRole,
  type UserRightsUser,
  type UserRightsUserOption,
  type UserRightsCapability,
  type RoleSaved,
  type RoleTrashed,
  type RoleMemberState,
  type CapabilityDefaultState
} from '../../../state_manager/data_models/user_rights_admin_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ServiceResult<T> {
  data: T | null
  error: string | null
}

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

async function request<T>(
  url: string,
  method: string,
  accessToken: string | null,
  parse: (data: unknown) => T,
  fallback: string,
  body?: unknown
): Promise<ServiceResult<T>> {
  try {
    const response = await fetch(url, {
      method,
      headers: authHeaders(accessToken),
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    if (!response.ok) return { data: null, error: await readBackendErrorMessage(response, fallback) }
    const json = await readJsonBody<{ data?: unknown }>(response)
    return { data: parse(json?.data), error: null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : fallback }
  }
}

export const fetchRoles = (accessToken: string | null): Promise<ServiceResult<RoleModel[]>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.ROLES),
    API_ROUTES.CONFIG.ROLES.method,
    accessToken,
    (data) => RoleModelSchema.array().parse(data),
    'Failed to load the roles'
  )

export const fetchCapabilityOverrides = (
  capabilityKey: string,
  accessToken: string | null
): Promise<ServiceResult<CapabilityOverridesModel>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CAPABILITY_OVERRIDES, { capabilityKey }),
    API_ROUTES.CONFIG.CAPABILITY_OVERRIDES.method,
    accessToken,
    (data) => CapabilityOverridesModelSchema.parse(data),
    'Failed to load who may move records between stages'
  )

// roleIds null: the record follows the capability's default rule; []: admins only.
export const saveCapabilityOverride = (
  capabilityKey: string,
  scopeId: string,
  roleIds: string[] | null,
  accessToken: string | null
): Promise<ServiceResult<CapabilityOverrideState>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CAPABILITY_OVERRIDE_SET, { capabilityKey, scopeId }),
    API_ROUTES.CONFIG.CAPABILITY_OVERRIDE_SET.method,
    accessToken,
    (data) => CapabilityOverrideStateSchema.parse(data),
    'Failed to save who may move records into or out of this stage',
    { data: { role_ids: roleIds } }
  )

// ── Configuration -> Users & Roles ──────────────────────────────────────────

const url = (route: RouteDefinition, params?: Record<string, string>): string =>
  buildFullUrl(BACKEND_BASE_URL, route, params)

export const fetchUserRightsRoles = (accessToken: string | null): Promise<ServiceResult<UserRightsRole[]>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_ROLES),
    API_ROUTES.CONFIG.USER_RIGHTS_ROLES.method,
    accessToken,
    (data) => UserRightsRoleSchema.array().parse(data),
    'Failed to load the roles'
  )

export const fetchUserRightsUsers = (accessToken: string | null): Promise<ServiceResult<UserRightsUser[]>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_USERS),
    API_ROUTES.CONFIG.USER_RIGHTS_USERS.method,
    accessToken,
    (data) => UserRightsUserSchema.array().parse(data),
    'Failed to load the users'
  )

export const fetchUserRightsUserOptions = (
  accessToken: string | null
): Promise<ServiceResult<UserRightsUserOption[]>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_USER_OPTIONS),
    API_ROUTES.CONFIG.USER_RIGHTS_USER_OPTIONS.method,
    accessToken,
    (data) => UserRightsUserOptionSchema.array().parse(data),
    'Failed to load the users'
  )

export const fetchUserRightsCapabilities = (
  accessToken: string | null
): Promise<ServiceResult<UserRightsCapability[]>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_CAPABILITIES),
    API_ROUTES.CONFIG.USER_RIGHTS_CAPABILITIES.method,
    accessToken,
    (data) => UserRightsCapabilitySchema.array().parse(data),
    'Failed to load the rights'
  )

export interface RoleFields {
  name?: string
  description?: string
  sort_order?: number
}

export const createRole = (fields: RoleFields, accessToken: string | null): Promise<ServiceResult<RoleSaved>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_ROLE_CREATE),
    API_ROUTES.CONFIG.USER_RIGHTS_ROLE_CREATE.method,
    accessToken,
    (data) => RoleSavedSchema.parse(data),
    'Failed to create the role',
    { data: fields }
  )

export const updateRole = (
  roleId: string,
  fields: RoleFields,
  accessToken: string | null
): Promise<ServiceResult<RoleSaved>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_ROLE_UPDATE, { roleId }),
    API_ROUTES.CONFIG.USER_RIGHTS_ROLE_UPDATE.method,
    accessToken,
    (data) => RoleSavedSchema.parse(data),
    'Failed to update the role',
    { data: fields }
  )

export const deleteRole = (roleId: string, accessToken: string | null): Promise<ServiceResult<RoleTrashed>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_ROLE_DELETE, { roleId }),
    API_ROUTES.CONFIG.USER_RIGHTS_ROLE_DELETE.method,
    accessToken,
    (data) => RoleTrashedSchema.parse(data),
    'Failed to remove the role'
  )

/** isMember true adds the user to the role; false takes them off it. */
export const setRoleMember = (
  roleId: string,
  systemUserId: string,
  isMember: boolean,
  accessToken: string | null
): Promise<ServiceResult<RoleMemberState>> => {
  const route = isMember ? API_ROUTES.CONFIG.USER_RIGHTS_ROLE_MEMBER_ADD : API_ROUTES.CONFIG.USER_RIGHTS_ROLE_MEMBER_REMOVE
  return request(
    url(route, { roleId, systemUserId }),
    route.method,
    accessToken,
    (data) => RoleMemberStateSchema.parse(data),
    isMember ? 'Failed to add the user to the role' : 'Failed to take the user off the role'
  )
}

/** roleIds [] is admins only. */
export const saveCapabilityDefault = (
  capabilityKey: string,
  roleIds: string[],
  accessToken: string | null
): Promise<ServiceResult<CapabilityDefaultState>> =>
  request(
    url(API_ROUTES.CONFIG.USER_RIGHTS_CAPABILITY_DEFAULT_SET, { capabilityKey }),
    API_ROUTES.CONFIG.USER_RIGHTS_CAPABILITY_DEFAULT_SET.method,
    accessToken,
    (data) => CapabilityDefaultStateSchema.parse(data),
    'Failed to save who may do this',
    { data: { role_ids: roleIds } }
  )
